import React from "react";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import {
  ttKpiChipSx,
  ttResponsiveTableSx,
  ttSectionTitleSx,
  ttTableContainerSx,
} from "./timeTrackingStyles";
import { MobileRecordCard } from "./TimeTrackingMobileCards";

function formatDays(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

const VACATION_LABELS = {
  section: (year) => `Saldo de vacaciones ${year}`,
  balanceYear: (year) => `Saldo ${year}`,
  calendarDays: (days) => `${formatDays(days)} días naturales`,
  perYear: (days) => `${formatDays(days)} / año`,
  total: "Total",
  used: "Consumidos",
  pending: "Pendientes",
  remaining: "Disponibles",
  previousYear: "Año anterior",
  currentYear: (year) => `Año ${year}`,
  employee: "Empleado",
  hint: "Los días del calendario no se vuelven a restar: el saldo parte de Sesame. Las solicitudes nuevas descuentan primero el año anterior y luego el año en curso.",
};

function buildBalanceStats(balance, theme, year) {
  const carryUsed = Number(balance.carryInUsed) || 0;
  const carryLeft = Number(balance.carryInRemaining) || 0;
  const pending = Number(balance.vacationDaysPendingApproval) || 0;
  const stats = [
    {
      key: "previous",
      label: VACATION_LABELS.previousYear,
      value: carryLeft,
      caption:
        carryUsed > 0
          ? `${formatDays(carryUsed)} usados este año`
          : "Nada pendiente de 2025",
      color: "#fb8c00",
      icon: EventAvailableOutlinedIcon,
      highlight: carryLeft > 0 || carryUsed > 0,
    },
    {
      key: "current",
      label: VACATION_LABELS.currentYear(year),
      value: balance.currentYearRemaining ?? balance.vacationDaysRemaining,
      caption: "por disfrutar",
      color: theme.palette.info.main,
      icon: HourglassTopOutlinedIcon,
      highlight: true,
    },
    {
      key: "used",
      label: VACATION_LABELS.used,
      value: balance.vacationDaysUsed,
      caption: "ya descontados",
      color: theme.palette.success.main,
      icon: BeachAccessOutlinedIcon,
    },
    {
      key: "pending",
      label: VACATION_LABELS.pending,
      value: pending,
      caption: pending > 0 ? "por aprobar · círculo vacío" : "nada por aprobar",
      color: theme.palette.warning.main,
      icon: PendingActionsOutlinedIcon,
      highlight: pending > 0,
    },
  ];
  return stats;
}

function VacationUsageBar({ balance, theme }) {
  const total = Number(balance?.vacationDaysTotal) || 0;
  if (total <= 0) return null;

  const usedPct = Math.min(
    100,
    (Number(balance.vacationDaysUsed) / total) * 100,
  );
  const pendingPct = Math.min(
    100 - usedPct,
    (Number(balance.vacationDaysPendingApproval) / total) * 100,
  );

  return (
    <Box sx={{ width: "100%", mt: 1.25 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700 }}
        >
          Consumo
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 800, color: theme.palette.info.main }}
        >
          {formatDays(balance.vacationDaysRemaining)} disponibles
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "flex",
          height: 6,
          borderRadius: 99,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.divider, 0.35),
        }}
      >
        <Box
          sx={{
            width: `${usedPct}%`,
            bgcolor: theme.palette.success.main,
            transition: "width 0.35s ease",
          }}
        />
        <Box
          sx={{
            width: `${pendingPct}%`,
            bgcolor: theme.palette.warning.main,
            transition: "width 0.35s ease",
          }}
        />
      </Box>
    </Box>
  );
}

function VacationStatTile({ stat, theme, compact = false }) {
  const Icon = stat.icon;
  return (
    <Box
      sx={{
        p: compact ? 1 : 1.25,
        borderRadius: 2,
        textAlign: "left",
        bgcolor: stat.highlight
          ? alpha(stat.color, 0.12)
          : alpha(stat.color, 0.07),
        border: "1px solid",
        borderColor: stat.highlight
          ? alpha(stat.color, 0.32)
          : alpha(stat.color, 0.18),
        boxShadow: stat.highlight
          ? `0 4px 16px ${alpha(stat.color, 0.12)}`
          : "none",
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        {Icon ? (
          <Icon
            sx={{
              fontSize: compact ? 15 : 16,
              color: stat.color,
              opacity: 0.9,
            }}
          />
        ) : null}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.35,
            textTransform: "uppercase",
            color: "text.secondary",
            fontSize: compact ? "0.62rem" : "0.65rem",
            lineHeight: 1.1,
          }}
        >
          {stat.label}
        </Typography>
      </Stack>
      <Typography
        variant={compact ? "h6" : "h5"}
        sx={{
          fontWeight: 800,
          color: stat.color,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatDays(stat.value)}
      </Typography>
      {stat.caption ? (
        <Typography
          variant="caption"
          sx={{
            mt: 0.4,
            display: "block",
            color: stat.highlight ? stat.color : "text.secondary",
            fontWeight: 700,
            fontSize: compact ? "0.58rem" : "0.65rem",
            lineHeight: 1.25,
          }}
        >
          {stat.caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function VacationPolicyNotice({ balance }) {
  if (!balance?.policyNotice) return null;
  return (
    <Alert
      severity="error"
      sx={{
        mt: 1.25,
        borderRadius: 2,
        py: 0.5,
        "& .MuiAlert-message": { fontWeight: 700, fontSize: "0.78rem" },
      }}
    >
      {balance.policyNotice}
    </Alert>
  );
}

function VacationStatGrid({ balance, theme, compact = false, year }) {
  const stats = buildBalanceStats(balance, theme, year);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: compact ? 0.75 : 1,
        width: "100%",
      }}
    >
      {stats.map((stat) => (
        <VacationStatTile
          key={stat.key}
          stat={stat}
          theme={theme}
          compact={compact}
        />
      ))}
    </Box>
  );
}

export function VacationBalanceCompact({ balance, year }) {
  const theme = useTheme();
  if (!balance) return null;

  return (
    <Box
      sx={{
        width: "100%",
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: alpha(theme.palette.info.main, 0.05),
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        boxShadow: `0 4px 18px ${alpha(theme.palette.info.main, 0.08)}`,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.25, rowGap: 0.5 }}
      >
        <BeachAccessOutlinedIcon
          sx={{ fontSize: 18, color: theme.palette.info.main }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.45,
            textTransform: "uppercase",
          }}
        >
          Vacaciones {year}
        </Typography>
        <Chip
          label={VACATION_LABELS.calendarDays(balance.vacationDaysTotal)}
          size="small"
          sx={{ ...ttKpiChipSx, height: 22, fontSize: "0.65rem" }}
        />
      </Stack>
      <VacationStatGrid balance={balance} theme={theme} compact year={year} />
      <VacationUsageBar balance={balance} theme={theme} />
      <VacationPolicyNotice balance={balance} />
    </Box>
  );
}

function BalanceCard({ stat, theme }) {
  const Icon = stat.icon;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: { xs: "auto", sm: 120 },
        p: 1.75,
        borderRadius: 2.5,
        bgcolor: stat.highlight
          ? alpha(stat.color, 0.1)
          : alpha(stat.color, 0.06),
        border: "1px solid",
        borderColor: stat.highlight
          ? alpha(stat.color, 0.3)
          : alpha(stat.color, 0.18),
        boxShadow: stat.highlight
          ? `0 6px 20px ${alpha(stat.color, 0.1)}`
          : "none",
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mb: 0.75 }}
      >
        {Icon ? <Icon sx={{ fontSize: 18, color: stat.color }} /> : null}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {stat.label}
        </Typography>
      </Stack>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: stat.color,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatDays(stat.value)}
      </Typography>
      {stat.caption ? (
        <Typography
          variant="caption"
          sx={{
            mt: 0.35,
            display: "block",
            color: stat.highlight ? stat.color : "text.secondary",
            fontWeight: 700,
          }}
        >
          {stat.caption}
        </Typography>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.35, display: "block" }}
        >
          días
        </Typography>
      )}
    </Box>
  );
}

export function VacationBalanceCards({ balance, year }) {
  const theme = useTheme();
  if (!balance) return null;

  const stats = buildBalanceStats(balance, theme, year);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.25, rowGap: 0.5 }}
      >
        <BeachAccessOutlinedIcon
          sx={{ fontSize: 18, color: theme.palette.info.main }}
        />
        <Typography variant="caption" sx={ttSectionTitleSx}>
          {VACATION_LABELS.section(year)} · días naturales
        </Typography>
        <Chip
          label={VACATION_LABELS.perYear(balance.vacationDaysTotal)}
          size="small"
          sx={ttKpiChipSx}
        />
      </Stack>
      <VacationPolicyNotice balance={balance} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        sx={{ mt: 1.25 }}
      >
        {stats.map((stat) => (
          <BalanceCard key={stat.key} stat={stat} theme={theme} />
        ))}
      </Stack>
      <VacationUsageBar balance={balance} theme={theme} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block", lineHeight: 1.45 }}
      >
        {VACATION_LABELS.hint}
      </Typography>
    </Box>
  );
}

export function VacationBalancesTable({ employees, year }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  if (!employees?.length) return null;

  if (isMobile) {
    return (
      <Box sx={{ mb: 2.5 }}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mb: 1.25 }}
        >
          <BeachAccessOutlinedIcon
            sx={{ fontSize: 18, color: theme.palette.info.main }}
          />
          <Typography variant="caption" sx={ttSectionTitleSx}>
            {VACATION_LABELS.section(year)}
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          {employees.map((emp) => (
            <MobileRecordCard
              key={emp.userId}
              theme={theme}
              accent={theme.palette.info.main}
              avatarName={emp.username}
              title={emp.username}
              subtitle={VACATION_LABELS.balanceYear(year)}
              statusLabel={`${formatDays(emp.vacationDaysRemaining)} disponibles`}
              statusColor="info"
            >
              <VacationStatGrid
                balance={emp}
                theme={theme}
                compact
                year={year}
              />
              <VacationUsageBar balance={emp} theme={theme} />
              <VacationPolicyNotice balance={emp} />
            </MobileRecordCard>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <BeachAccessOutlinedIcon
          sx={{ fontSize: 18, color: theme.palette.info.main }}
        />
        <Typography variant="caption" sx={ttSectionTitleSx}>
          {VACATION_LABELS.section(year)}
        </Typography>
      </Stack>
      {employees[0]?.policyNotice ? (
        <VacationPolicyNotice balance={employees[0]} />
      ) : null}
      <TableContainer sx={{ ...ttTableContainerSx(theme), mt: 1 }}>
        <Table size="small" sx={ttResponsiveTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>{VACATION_LABELS.employee}</TableCell>
              <TableCell align="right">
                {VACATION_LABELS.previousYear}
              </TableCell>
              <TableCell align="right">
                {VACATION_LABELS.currentYear(year)}
              </TableCell>
              <TableCell align="right">{VACATION_LABELS.used}</TableCell>
              <TableCell align="right">{VACATION_LABELS.pending}</TableCell>
              <TableCell align="right">{VACATION_LABELS.remaining}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((emp) => {
              const total = Number(emp.vacationDaysTotal) || 0;
              const usedPct =
                total > 0 ? (Number(emp.vacationDaysUsed) / total) * 100 : 0;

              return (
                <TableRow key={emp.userId} hover>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                    <Stack spacing={0.75}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {emp.username}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, usedPct)}
                        sx={{
                          height: 4,
                          borderRadius: 99,
                          bgcolor: alpha(theme.palette.divider, 0.35),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 99,
                            bgcolor: theme.palette.success.main,
                          },
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "#fb8c00", fontWeight: 800 }}
                  >
                    {formatDays(emp.carryInRemaining ?? 0)}
                    {emp.carryInUsed ? (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: "#fb8c00", fontWeight: 700 }}
                      >
                        {formatDays(emp.carryInUsed)} usados
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatDays(
                      emp.currentYearRemaining ?? emp.vacationDaysRemaining,
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "success.main", fontWeight: 700 }}
                  >
                    {formatDays(emp.vacationDaysUsed)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "warning.main", fontWeight: 700 }}
                  >
                    {formatDays(emp.vacationDaysPendingApproval)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "info.main", fontWeight: 800 }}
                  >
                    {formatDays(emp.vacationDaysRemaining)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
